/**
 * Trivia Question Bank
 *
 * Contains 60 hard-coded questions across 6 categories.
 * The game works fully offline with this question bank.
 */

import { TriviaQuestion } from './types';

export const questionBank: TriviaQuestion[] = [
  // =========================================================================
  // SCIENCE (10 questions)
  // =========================================================================
  {
    id: 'sci-001',
    category: 'Science',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'What is the chemical symbol for water?',
    options: ['H2O', 'CO2', 'NaCl', 'O2'],
    correctAnswer: 'H2O',
    explanation: 'Water is composed of two hydrogen atoms and one oxygen atom.',
  },
  {
    id: 'sci-002',
    category: 'Science',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'What planet is known as the Red Planet?',
    options: ['Venus', 'Mars', 'Jupiter', 'Saturn'],
    correctAnswer: 'Mars',
    explanation: 'Mars appears red due to iron oxide (rust) on its surface.',
  },
  {
    id: 'sci-003',
    category: 'Science',
    type: 'true-false',
    difficulty: 'easy',
    question: 'The speed of light is approximately 300,000 km/s.',
    options: ['True', 'False'],
    correctAnswer: 'True',
    explanation: 'Light travels at approximately 299,792 km/s in a vacuum.',
  },
  {
    id: 'sci-004',
    category: 'Science',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'What is the powerhouse of the cell?',
    options: ['Nucleus', 'Ribosome', 'Mitochondria', 'Endoplasmic Reticulum'],
    correctAnswer: 'Mitochondria',
    explanation: 'Mitochondria generate most of the cell\'s supply of ATP, the energy currency.',
  },
  {
    id: 'sci-005',
    category: 'Science',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'What gas do plants absorb from the atmosphere?',
    options: ['Oxygen', 'Nitrogen', 'Carbon Dioxide', 'Hydrogen'],
    correctAnswer: 'Carbon Dioxide',
    explanation: 'Plants use CO2 in photosynthesis to produce glucose and oxygen.',
  },
  {
    id: 'sci-006',
    category: 'Science',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'What is the atomic number of gold?',
    options: ['47', '79', '82', '29'],
    correctAnswer: '79',
    explanation: 'Gold (Au) has atomic number 79 on the periodic table.',
  },
  {
    id: 'sci-007',
    category: 'Science',
    type: 'true-false',
    difficulty: 'medium',
    question: 'Electrons are larger than molecules.',
    options: ['True', 'False'],
    correctAnswer: 'False',
    explanation: 'Electrons are subatomic particles, much smaller than molecules.',
  },
  {
    id: 'sci-008',
    category: 'Science',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'How many bones are in the adult human body?',
    options: ['106', '206', '306', '186'],
    correctAnswer: '206',
    explanation: 'The adult human skeleton consists of 206 bones.',
  },
  {
    id: 'sci-009',
    category: 'Science',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'What is the half-life of Carbon-14?',
    options: ['1,000 years', '5,730 years', '10,000 years', '50,000 years'],
    correctAnswer: '5,730 years',
    explanation: 'Carbon-14 has a half-life of approximately 5,730 years, used in radiocarbon dating.',
  },
  {
    id: 'sci-010',
    category: 'Science',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Which organ is responsible for filtering blood in the human body?',
    options: ['Liver', 'Heart', 'Kidneys', 'Lungs'],
    correctAnswer: 'Kidneys',
    explanation: 'The kidneys filter waste products from the blood and produce urine.',
  },

  // =========================================================================
  // HISTORY (10 questions)
  // =========================================================================
  {
    id: 'his-001',
    category: 'History',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'In which year did World War II end?',
    options: ['1943', '1944', '1945', '1946'],
    correctAnswer: '1945',
    explanation: 'WWII ended in 1945 with the surrender of Japan on September 2.',
  },
  {
    id: 'his-002',
    category: 'History',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Who was the first President of the United States?',
    options: ['Thomas Jefferson', 'George Washington', 'John Adams', 'Benjamin Franklin'],
    correctAnswer: 'George Washington',
    explanation: 'George Washington served as the first president from 1789 to 1797.',
  },
  {
    id: 'his-003',
    category: 'History',
    type: 'true-false',
    difficulty: 'easy',
    question: 'The Great Wall of China is visible from space with the naked eye.',
    options: ['True', 'False'],
    correctAnswer: 'False',
    explanation: 'This is a common myth. The wall is too narrow to be seen from space without aid.',
  },
  {
    id: 'his-004',
    category: 'History',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'Which ancient civilization built Machu Picchu?',
    options: ['Aztec', 'Maya', 'Inca', 'Olmec'],
    correctAnswer: 'Inca',
    explanation: 'Machu Picchu was built by the Inca Empire in the 15th century.',
  },
  {
    id: 'his-005',
    category: 'History',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'The French Revolution began in which year?',
    options: ['1776', '1789', '1799', '1804'],
    correctAnswer: '1789',
    explanation: 'The French Revolution began in 1789 with the storming of the Bastille.',
  },
  {
    id: 'his-006',
    category: 'History',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'Who was the last pharaoh of ancient Egypt?',
    options: ['Nefertiti', 'Cleopatra VII', 'Hatshepsut', 'Tutankhamun'],
    correctAnswer: 'Cleopatra VII',
    explanation: 'Cleopatra VII was the last active ruler of the Ptolemaic Kingdom of Egypt.',
  },
  {
    id: 'his-007',
    category: 'History',
    type: 'true-false',
    difficulty: 'medium',
    question: 'The Roman Empire fell in 476 AD.',
    options: ['True', 'False'],
    correctAnswer: 'True',
    explanation: 'The Western Roman Empire fell in 476 AD when Romulus Augustulus was deposed.',
  },
  {
    id: 'his-008',
    category: 'History',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'Which country was the first to land a man on the Moon?',
    options: ['Soviet Union', 'United States', 'China', 'United Kingdom'],
    correctAnswer: 'United States',
    explanation: 'Apollo 11, a US mission, landed the first humans on the Moon in 1969.',
  },
  {
    id: 'his-009',
    category: 'History',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'The Treaty of Westphalia (1648) ended which conflict?',
    options: ['Hundred Years War', 'Thirty Years War', 'Seven Years War', 'Napoleonic Wars'],
    correctAnswer: 'Thirty Years War',
    explanation: 'The Peace of Westphalia ended the Thirty Years War in Europe.',
  },
  {
    id: 'his-010',
    category: 'History',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Who wrote the Communist Manifesto?',
    options: ['Lenin', 'Stalin', 'Karl Marx', 'Trotsky'],
    correctAnswer: 'Karl Marx',
    explanation: 'Karl Marx (with Friedrich Engels) published the Communist Manifesto in 1848.',
  },

  // =========================================================================
  // GEOGRAPHY (10 questions)
  // =========================================================================
  {
    id: 'geo-001',
    category: 'Geography',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'What is the largest ocean on Earth?',
    options: ['Atlantic', 'Indian', 'Pacific', 'Arctic'],
    correctAnswer: 'Pacific',
    explanation: 'The Pacific Ocean covers about 63 million square miles.',
  },
  {
    id: 'geo-002',
    category: 'Geography',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'What is the capital of Australia?',
    options: ['Sydney', 'Melbourne', 'Canberra', 'Brisbane'],
    correctAnswer: 'Canberra',
    explanation: 'Canberra is the capital city, chosen as a compromise between Sydney and Melbourne.',
  },
  {
    id: 'geo-003',
    category: 'Geography',
    type: 'true-false',
    difficulty: 'easy',
    question: 'Mount Everest is the tallest mountain in the world.',
    options: ['True', 'False'],
    correctAnswer: 'True',
    explanation: 'At 8,849 meters, Mt. Everest is the highest point above sea level.',
  },
  {
    id: 'geo-004',
    category: 'Geography',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'Which country has the most time zones?',
    options: ['Russia', 'United States', 'France', 'China'],
    correctAnswer: 'France',
    explanation: 'France has 12 time zones due to its overseas territories.',
  },
  {
    id: 'geo-005',
    category: 'Geography',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'What is the longest river in the world?',
    options: ['Amazon', 'Nile', 'Mississippi', 'Yangtze'],
    correctAnswer: 'Nile',
    explanation: 'The Nile River is approximately 6,650 km long.',
  },
  {
    id: 'geo-006',
    category: 'Geography',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'Which continent is the Sahara Desert located on?',
    options: ['Asia', 'Australia', 'Africa', 'South America'],
    correctAnswer: 'Africa',
    explanation: 'The Sahara is the largest hot desert in the world, located in northern Africa.',
  },
  {
    id: 'geo-007',
    category: 'Geography',
    type: 'true-false',
    difficulty: 'hard',
    question: 'Iceland is covered mostly in ice.',
    options: ['True', 'False'],
    correctAnswer: 'False',
    explanation: 'Despite its name, Iceland is mostly green. Greenland is the one covered in ice.',
  },
  {
    id: 'geo-008',
    category: 'Geography',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'What is the smallest country in the world by area?',
    options: ['Monaco', 'Vatican City', 'San Marino', 'Liechtenstein'],
    correctAnswer: 'Vatican City',
    explanation: 'Vatican City is only about 0.44 km2 in area.',
  },
  {
    id: 'geo-009',
    category: 'Geography',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'Which strait separates Europe from Africa?',
    options: ['Strait of Hormuz', 'Strait of Gibraltar', 'Strait of Malacca', 'Bosphorus'],
    correctAnswer: 'Strait of Gibraltar',
    explanation: 'The Strait of Gibraltar connects the Atlantic Ocean to the Mediterranean Sea.',
  },
  {
    id: 'geo-010',
    category: 'Geography',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Which country is known as the Land of the Rising Sun?',
    options: ['China', 'South Korea', 'Japan', 'Thailand'],
    correctAnswer: 'Japan',
    explanation: 'Japan is called "Nihon" or "Nippon" meaning "origin of the sun".',
  },

  // =========================================================================
  // ENTERTAINMENT (10 questions)
  // =========================================================================
  {
    id: 'ent-001',
    category: 'Entertainment',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'Who directed the movie "Jurassic Park"?',
    options: ['James Cameron', 'Steven Spielberg', 'George Lucas', 'Ridley Scott'],
    correctAnswer: 'Steven Spielberg',
    explanation: 'Steven Spielberg directed the 1993 blockbuster Jurassic Park.',
  },
  {
    id: 'ent-002',
    category: 'Entertainment',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Which band released the album "Abbey Road"?',
    options: ['The Rolling Stones', 'The Beatles', 'Led Zeppelin', 'Pink Floyd'],
    correctAnswer: 'The Beatles',
    explanation: 'Abbey Road was released by The Beatles in 1969.',
  },
  {
    id: 'ent-003',
    category: 'Entertainment',
    type: 'true-false',
    difficulty: 'easy',
    question: 'The character Harry Potter was created by J.K. Rowling.',
    options: ['True', 'False'],
    correctAnswer: 'True',
    explanation: 'J.K. Rowling wrote the Harry Potter series starting in 1997.',
  },
  {
    id: 'ent-004',
    category: 'Entertainment',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'Which film won the Academy Award for Best Picture in 2020?',
    options: ['1917', 'Parasite', 'Joker', 'Once Upon a Time in Hollywood'],
    correctAnswer: 'Parasite',
    explanation: 'Parasite (2019) by Bong Joon-ho was the first non-English film to win Best Picture.',
  },
  {
    id: 'ent-005',
    category: 'Entertainment',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'What is the highest-grossing film of all time (not adjusted for inflation)?',
    options: ['Avengers: Endgame', 'Avatar', 'Titanic', 'Star Wars: The Force Awakens'],
    correctAnswer: 'Avatar',
    explanation: 'Avatar (2009) holds the record with over $2.9 billion worldwide.',
  },
  {
    id: 'ent-006',
    category: 'Entertainment',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'Which Disney movie features a character named Simba?',
    options: ['Aladdin', 'The Lion King', 'Bambi', 'Tarzan'],
    correctAnswer: 'The Lion King',
    explanation: 'Simba is the main character in The Lion King (1994).',
  },
  {
    id: 'ent-007',
    category: 'Entertainment',
    type: 'true-false',
    difficulty: 'medium',
    question: 'The TV show "Friends" ran for 10 seasons.',
    options: ['True', 'False'],
    correctAnswer: 'True',
    explanation: 'Friends aired from 1994 to 2004, spanning 10 seasons and 236 episodes.',
  },
  {
    id: 'ent-008',
    category: 'Entertainment',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'Who composed the music for the film "Inception"?',
    options: ['John Williams', 'Hans Zimmer', 'Howard Shore', 'Danny Elfman'],
    correctAnswer: 'Hans Zimmer',
    explanation: 'Hans Zimmer composed the iconic score for Christopher Nolan\'s Inception (2010).',
  },
  {
    id: 'ent-009',
    category: 'Entertainment',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'What year was the first iPhone released?',
    options: ['2005', '2006', '2007', '2008'],
    correctAnswer: '2007',
    explanation: 'The first iPhone was released by Apple on June 29, 2007.',
  },
  {
    id: 'ent-010',
    category: 'Entertainment',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'Which video game features a plumber named Mario?',
    options: ['Sonic the Hedgehog', 'Super Mario Bros', 'Pac-Man', 'Donkey Kong'],
    correctAnswer: 'Super Mario Bros',
    explanation: 'Mario first appeared in Donkey Kong (1981) and stars in the Super Mario series.',
  },

  // =========================================================================
  // SPORTS (10 questions)
  // =========================================================================
  {
    id: 'spo-001',
    category: 'Sports',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'How many players are on a standard soccer team on the field?',
    options: ['9', '10', '11', '12'],
    correctAnswer: '11',
    explanation: 'Each team fields 11 players including the goalkeeper.',
  },
  {
    id: 'spo-002',
    category: 'Sports',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'In which sport would you perform a "slam dunk"?',
    options: ['Volleyball', 'Basketball', 'Tennis', 'Baseball'],
    correctAnswer: 'Basketball',
    explanation: 'A slam dunk is a basketball move where a player jumps and puts the ball through the hoop.',
  },
  {
    id: 'spo-003',
    category: 'Sports',
    type: 'true-false',
    difficulty: 'easy',
    question: 'The Olympic Games are held every 4 years.',
    options: ['True', 'False'],
    correctAnswer: 'True',
    explanation: 'Both the Summer and Winter Olympics are held every four years.',
  },
  {
    id: 'spo-004',
    category: 'Sports',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'Who holds the record for most Grand Slam singles titles in tennis (men)?',
    options: ['Roger Federer', 'Rafael Nadal', 'Novak Djokovic', 'Pete Sampras'],
    correctAnswer: 'Novak Djokovic',
    explanation: 'Novak Djokovic holds the record with 24 Grand Slam singles titles.',
  },
  {
    id: 'spo-005',
    category: 'Sports',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Which country won the FIFA World Cup in 2018?',
    options: ['Brazil', 'Germany', 'France', 'Argentina'],
    correctAnswer: 'France',
    explanation: 'France won the 2018 FIFA World Cup held in Russia.',
  },
  {
    id: 'spo-006',
    category: 'Sports',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'What sport is played at Wimbledon?',
    options: ['Cricket', 'Tennis', 'Golf', 'Rugby'],
    correctAnswer: 'Tennis',
    explanation: 'Wimbledon is the oldest and most prestigious tennis tournament in the world.',
  },
  {
    id: 'spo-007',
    category: 'Sports',
    type: 'true-false',
    difficulty: 'medium',
    question: 'A marathon is exactly 26.2 miles long.',
    options: ['True', 'False'],
    correctAnswer: 'True',
    explanation: 'A marathon is 26 miles and 385 yards (26.2 miles or 42.195 km).',
  },
  {
    id: 'spo-008',
    category: 'Sports',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'In cricket, how many balls are in a standard over?',
    options: ['4', '5', '6', '8'],
    correctAnswer: '6',
    explanation: 'A standard over in cricket consists of 6 legal deliveries.',
  },
  {
    id: 'spo-009',
    category: 'Sports',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Which team has won the most NBA championships?',
    options: ['Chicago Bulls', 'Los Angeles Lakers', 'Boston Celtics', 'Golden State Warriors'],
    correctAnswer: 'Boston Celtics',
    explanation: 'The Boston Celtics have won 18 NBA championships, the most of any franchise.',
  },
  {
    id: 'spo-010',
    category: 'Sports',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'What is the diameter of a basketball hoop in inches?',
    options: ['16', '18', '20', '22'],
    correctAnswer: '18',
    explanation: 'A regulation basketball hoop has an inner diameter of 18 inches (46 cm).',
  },

  // =========================================================================
  // TECHNOLOGY (10 questions)
  // =========================================================================
  {
    id: 'tech-001',
    category: 'Technology',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'What does HTML stand for?',
    options: ['Hyper Text Markup Language', 'High Tech Modern Language', 'Home Tool Markup Language', 'Hyper Transfer Markup Language'],
    correctAnswer: 'Hyper Text Markup Language',
    explanation: 'HTML (HyperText Markup Language) is the standard language for creating web pages.',
  },
  {
    id: 'tech-002',
    category: 'Technology',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Who is considered the father of computer science?',
    options: ['Bill Gates', 'Alan Turing', 'Steve Jobs', 'Tim Berners-Lee'],
    correctAnswer: 'Alan Turing',
    explanation: 'Alan Turing formalized concepts of algorithms and computation with his Turing machine.',
  },
  {
    id: 'tech-003',
    category: 'Technology',
    type: 'true-false',
    difficulty: 'easy',
    question: 'The first computer mouse was made of wood.',
    options: ['True', 'False'],
    correctAnswer: 'True',
    explanation: 'Douglas Engelbart invented the first mouse in 1964; its shell was made of wood.',
  },
  {
    id: 'tech-004',
    category: 'Technology',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'What year was the World Wide Web invented?',
    options: ['1985', '1989', '1993', '1995'],
    correctAnswer: '1989',
    explanation: 'Tim Berners-Lee proposed the World Wide Web in 1989 at CERN.',
  },
  {
    id: 'tech-005',
    category: 'Technology',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Which programming language is known as the "language of the web"?',
    options: ['Python', 'Java', 'JavaScript', 'C++'],
    correctAnswer: 'JavaScript',
    explanation: 'JavaScript is the primary language used for web development and browser scripting.',
  },
  {
    id: 'tech-006',
    category: 'Technology',
    type: 'multiple-choice',
    difficulty: 'easy',
    question: 'What does CPU stand for?',
    options: ['Central Processing Unit', 'Computer Personal Unit', 'Central Program Utility', 'Core Processing Unit'],
    correctAnswer: 'Central Processing Unit',
    explanation: 'The CPU is the primary component of a computer that performs instructions.',
  },
  {
    id: 'tech-007',
    category: 'Technology',
    type: 'true-false',
    difficulty: 'medium',
    question: 'Bitcoin was created by Satoshi Nakamoto.',
    options: ['True', 'False'],
    correctAnswer: 'True',
    explanation: 'Bitcoin was introduced in a 2008 whitepaper by the pseudonymous Satoshi Nakamoto.',
  },
  {
    id: 'tech-008',
    category: 'Technology',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'What does the acronym API stand for?',
    options: ['Application Programming Interface', 'Advanced Program Integration', 'Automated Processing Instruction', 'Application Process Integration'],
    correctAnswer: 'Application Programming Interface',
    explanation: 'An API defines how software components should interact with each other.',
  },
  {
    id: 'tech-009',
    category: 'Technology',
    type: 'multiple-choice',
    difficulty: 'medium',
    question: 'Which company developed the Android operating system?',
    options: ['Apple', 'Microsoft', 'Google', 'Samsung'],
    correctAnswer: 'Google',
    explanation: 'Android was developed by Android Inc., which was acquired by Google in 2005.',
  },
  {
    id: 'tech-010',
    category: 'Technology',
    type: 'multiple-choice',
    difficulty: 'hard',
    question: 'How many bits are in a byte?',
    options: ['4', '8', '16', '32'],
    correctAnswer: '8',
    explanation: 'A byte consists of 8 bits and can represent values from 0 to 255.',
  },
];
